import { useEffect, useState, useCallback } from 'react';
import { useProjectStore } from '@/store/projectStore.ts';
import { useCommentStore } from '@/store/commentStore.ts';
import { loadProject } from '@/store/actions.ts';
import { getSessionComments, getSessionApprovals, validateSession, StatusError } from '@/api/reviewApi.ts';
import { useUiStore } from '@/store/uiStore.ts';
import { getSupabase } from '@/api/supabaseClient.ts';
import { extractDisplayName } from '@/api/accountApi.ts';
import type { TimelineProject, Comment, Approval } from '@/types/timeline.ts';
import { normalizeApprovalStatus } from '@/types/timeline.ts';
import { uuidToNegId } from '@/previs/commentId.ts';
import { isBlobManifest, applyBlobManifestUrls } from '@/utils/blobManifest.ts';
import type { BlobManifest } from '@/utils/blobManifest.ts';
import { PrevisProLogo } from '@/components/Landing/PrevisProLogo.tsx';
import styles from './ReviewLoader.module.css';

type LoadState = 'initializing' | 'password-prompt' | 'validating' | 'loading' | 'error' | 'expired';

export function ReviewLoader({ sessionId }: { sessionId: string }) {
  const [state, setState] = useState<LoadState>('initializing');
  const [errorMsg, setErrorMsg] = useState('');
  const [password, setPassword] = useState('');
  const [reviewerName, setReviewerName] = useState('');
  const [statusMsg, setStatusMsg] = useState('Connecting...');

  const setReadOnly = useProjectStore(s => s.setReadOnly);
  const setSessionId = useProjectStore(s => s.setSessionId);
  const setIsCreator = useProjectStore(s => s.setIsCreator);

  // On mount: read display name from existing session and go to password prompt
  useEffect(() => {
    let cancelled = false;

    async function init() {
      const sb = await getSupabase();
      if (!sb) {
        if (!cancelled) {
          setErrorMsg('Supabase not configured');
          setState('error');
        }
        return;
      }

      const { data: { session } } = await sb.auth.getSession();
      if (cancelled) return;

      if (session?.user) {
        const name = extractDisplayName(session.user);
        setReviewerName(name);
        useCommentStore.getState().setReviewerName(name);
      }
      setState('password-prompt');
    }

    init();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Step 2: Load project data after validation returns session
  const loadFromSession = useCallback(async (session: { project_json: unknown; id: string; is_creator?: boolean }) => {
    const projectJson = session.project_json as TimelineProject;
    if (!projectJson || !projectJson.scenes) {
      setErrorMsg('Invalid session data');
      setState('error');
      return;
    }

    // Ensure arrays exist
    projectJson.transitions = projectJson.transitions ?? [];
    projectJson.audioClips = projectJson.audioClips ?? [];
    projectJson.videoShots = projectJson.videoShots ?? [];

    // Fetch audio bytes from CDN URLs or decode inline base64, stash in blob store
    const { stashBlob } = await import('@/previs/audioBlobStore.ts');
    for (const clip of projectJson.audioClips as Array<{ rawBase64?: string; mediaUrl?: string; id?: number }>) {
      if (clip.mediaUrl) {
        try {
          const resp = await fetch(clip.mediaUrl);
          if (resp.ok) {
            stashBlob(clip.id!, new Uint8Array(await resp.arrayBuffer()));
          }
        } catch (e) {
          console.warn(`Failed to fetch audio clip ${clip.id} from CDN`, e);
        }
      } else if (clip.rawBase64) {
        try {
          const dataUrl: string = clip.rawBase64;
          const b64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
          const binary = atob(b64);
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
          stashBlob(clip.id!, bytes);
        } catch (e) {
          console.warn(`Failed to decode audio clip ${clip.id} from base64`, e);
        }
      }
    }
    // Map videoDataUrl (export format) → videoObjectUrl (store/renderer format)
    for (const scene of projectJson.scenes) {
      for (const shot of scene.shots as Array<{ videoObjectUrl?: string; videoDataUrl?: string }>) {
        if (!shot.videoObjectUrl && shot.videoDataUrl) {
          shot.videoObjectUrl = shot.videoDataUrl;
          delete shot.videoDataUrl;
        }
      }
    }

    projectJson.comments = projectJson.comments ?? [];
    projectJson.approvals = projectJson.approvals ?? [];

    // Recompute hasScreenplay for sessions shared before this field was exported
    if (projectJson.hasScreenplay == null) {
      projectJson.hasScreenplay = projectJson.scenes.some(
        s => !!(s as any).screenplayText || !!(s as any).screenplay,
      );
    }

    // Fetch remote comments and approvals (now allowed by RLS)
    const [remoteComments, remoteApprovals] = await Promise.all([
      getSessionComments(sessionId),
      getSessionApprovals(sessionId),
    ]);

    // Merge remote comments into project (dedup by Supabase UUID hash)
    const existingIds = new Set(projectJson.comments.map((c: Comment) => c.id));
    const mergedComments: Comment[] = [
      ...projectJson.comments,
      ...remoteComments
        .filter(rc => !existingIds.has(uuidToNegId(rc.id)))
        .map(rc => ({
          id: uuidToNegId(rc.id),
          shotId: rc.shot_id,
          timecode: rc.timecode,
          author: rc.author,
          text: rc.text,
          createdAt: rc.created_at,
          resolvedAt: rc.resolved_at,
          parentCommentId: null,
          annotationSvg: rc.annotation_svg ?? null,
        })),
    ];

    // Semantic dedup: match by (shotId, revision) to prevent local/remote ID mismatch duplication
    const existingApprovalKeys = new Set(
      projectJson.approvals.map((a: Approval) => `${a.shotId}:${a.revision}`),
    );
    const mergedApprovals: Approval[] = [
      ...projectJson.approvals,
      ...remoteApprovals
        .filter(ra => !existingApprovalKeys.has(`${ra.shot_id}:${ra.revision}`))
        .map(ra => ({
          id: uuidToNegId(ra.id),
          shotId: ra.shot_id,
          status: normalizeApprovalStatus(ra.status),
          approvedBy: ra.approved_by,
          approvedAt: ra.approved_at ?? new Date().toISOString(),
          notes: ra.notes,
          revision: ra.revision,
        })),
    ];

    projectJson.comments = mergedComments;
    projectJson.approvals = mergedApprovals;

    await loadProject(projectJson);
    useProjectStore.setState({ activeSceneId: null }); // Start in All Scenes view
    setReadOnly(true);
    setSessionId(sessionId);
    setIsCreator(session.is_creator ?? false);
    // Auto-open comment panel — skip on mobile where it's full-screen
    const mobileQ = '(max-width: 500px), ((pointer: coarse) and (hover: none) and (max-height: 500px))';
    const isMobile = window.matchMedia(mobileQ).matches || useUiStore.getState().forceMobile;
    if (!isMobile) {
      useUiStore.getState().setCommentPanelOpen(true);
    }
  }, [sessionId, setReadOnly, setSessionId, setIsCreator]);

  /** Load a blob-backed session: download .previs from Vercel Blob, parse via sql.js. */
  const loadFromBlobManifest = useCallback(async (
    manifest: BlobManifest,
    sid: string,
    isCreator: boolean,
  ) => {
    setStatusMsg('Downloading project file...');
    const resp = await fetch(manifest.previs_url);
    if (!resp.ok) {
      throw new Error(`Failed to download project: ${resp.status}`);
    }
    const buffer = await resp.arrayBuffer();

    setStatusMsg('Parsing project...');
    const { loadPrevisFile } = await import('@/previs/previsLoader.ts');
    const project = await loadPrevisFile(buffer);

    // Apply CDN URLs from manifest onto loaded project
    applyBlobManifestUrls(project, manifest);

    // Fetch comments/approvals
    setStatusMsg('Loading comments...');
    const [remoteComments, remoteApprovals] = await Promise.all([
      getSessionComments(sid),
      getSessionApprovals(sid),
    ]);

    project.comments = remoteComments.map(rc => ({
      id: uuidToNegId(rc.id),
      shotId: rc.shot_id,
      timecode: rc.timecode,
      author: rc.author,
      text: rc.text,
      createdAt: rc.created_at,
      resolvedAt: rc.resolved_at,
      parentCommentId: null,
      annotationSvg: rc.annotation_svg ?? null,
    }));

    project.approvals = remoteApprovals.map(ra => ({
      id: uuidToNegId(ra.id),
      shotId: ra.shot_id,
      status: normalizeApprovalStatus(ra.status),
      approvedBy: ra.approved_by,
      approvedAt: ra.approved_at ?? new Date().toISOString(),
      notes: ra.notes,
      revision: ra.revision,
    }));

    // Fetch audio blobs from CDN before loadProject so processAudioClips can decode them
    if (manifest.audio_urls?.length) {
      const audioClips = (project.audioClips ?? []).filter(c => c.audioFileUrl);
      if (audioClips.length > 0) {
        setStatusMsg('Loading audio...');
        const { stashBlob } = await import('@/previs/audioBlobStore.ts');
        await Promise.all(audioClips.map(async (clip) => {
          try {
            const audioResp = await fetch(clip.audioFileUrl!);
            stashBlob(clip.id, new Uint8Array(await audioResp.arrayBuffer()));
          } catch (err) {
            console.error(`[ReviewLoader] Failed to fetch audio for clip ${clip.id}:`, err);
          }
        }));
      }
    }

    await loadProject(project);
    useProjectStore.setState({ activeSceneId: null });
    setReadOnly(true);
    setSessionId(sid);
    setIsCreator(isCreator);

    const mobileQ = '(max-width: 500px), ((pointer: coarse) and (hover: none) and (max-height: 500px))';
    const isMobile = window.matchMedia(mobileQ).matches || useUiStore.getState().forceMobile;
    if (!isMobile) {
      useUiStore.getState().setCommentPanelOpen(true);
    }
  }, [setReadOnly, setSessionId, setIsCreator, setStatusMsg]);

  // Step 3: Handle password submit
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg('');
    setState('validating');

    try {
      const session = await validateSession(sessionId, password, reviewerName);
      setState('loading');
      if (isBlobManifest(session.project_json)) {
        await loadFromBlobManifest(session.project_json, sessionId, session.is_creator ?? false);
      } else {
        await loadFromSession(session);
      }
      if (session.revision) {
        useProjectStore.getState().setSessionRevision(session.revision);
      }
    } catch (err) {
      if (err instanceof StatusError && err.status === 403) {
        setErrorMsg('Incorrect password. Please try again.');
        setState('password-prompt');
      } else if (err instanceof StatusError && err.status === 410) {
        setState('expired');
      } else {
        setErrorMsg(err instanceof Error ? err.message : 'Failed to validate session');
        setState('password-prompt');
      }
    }
  }, [sessionId, password, reviewerName, loadFromSession, loadFromBlobManifest]);

  // ── Render states ──

  if (state === 'expired') {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.icon}>&#x23F0;</div>
          <h2 className={styles.title}>Session Expired</h2>
          <p className={styles.message}>
            This review session has expired or been revoked.
            Ask the project owner to share a new link.
          </p>
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.icon}>&#x26A0;</div>
          <h2 className={styles.title}>Error Loading Session</h2>
          <p className={styles.message}>{errorMsg}</p>
        </div>
      </div>
    );
  }

  if (state === 'initializing') {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.spinner} />
          <p className={styles.message}>{statusMsg}</p>
        </div>
      </div>
    );
  }

  if (state === 'password-prompt' || state === 'validating') {
    return (
      <div className={styles.container}>
        <div className={styles.brandedLayout}>
          <a href="https://www.previspro.com" target="_blank" rel="noopener noreferrer" className={styles.logoLink}>
            <PrevisProLogo className={styles.logo} />
          </a>

          <div className={styles.card}>
            <h2 className={styles.title}>Review Session</h2>
            <p className={styles.message}>
              Enter the password provided by the project owner.
            </p>
            {reviewerName && <span className={styles.reviewerBadge}>{reviewerName}</span>}
            <form className={styles.form} onSubmit={handleSubmit}>
              <input
                className={styles.input}
                type="password"
                placeholder="Session password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete="off"
                autoFocus
                style={{ fontFamily: 'var(--font-mono)', letterSpacing: '0.15em', textTransform: 'uppercase' }}
              />
              {errorMsg && <p className={styles.formError}>{errorMsg}</p>}
              <button
                className={styles.submitBtn}
                type="submit"
                disabled={state === 'validating' || !password.trim()}
              >
                {state === 'validating' ? 'Verifying...' : 'Join Review'}
              </button>
            </form>
          </div>

          <div className={styles.featuresGrid}>
            <div className={styles.featureItem}>
              <div className={styles.featureIcon}>✦</div>
              <div className={styles.featureTitle}>Latest Image and Video AIs</div>
              {/* 20260921 - Model line-up, sourced from src/ai/tokenCosts.ts FALLBACK_CATALOG (which
                  mirrors the edge function's MODEL_CATALOG). Kling / Veo / Wan were retired in the
                  2026-08 MiniMax H3 rollout (see aiStore.ts's auto-migration) — this is the first
                  screen every external reviewer sees, and it was naming three models the product no
                  longer offers. */}
              <div className={styles.featureDesc}>Nano Banana 2, FLUX, Seedream for images. MiniMax H3 for video. All in one place — pick the model that fits your shot.</div>
            </div>
            <div className={styles.featureItem}>
              <div className={styles.featureIcon}>♫</div>
              <div className={styles.featureTitle}>Full Audio Pipeline</div>
              <div className={styles.featureDesc}>ElevenLabs voices, music, and SFX. Record scratch dialogue. Every lane covered so your animatic sounds as good as it looks.</div>
            </div>
            <div className={styles.featureItem}>
              <div className={styles.featureIcon}>◎</div>
              <div className={styles.featureTitle}>Team Review &amp; Sharing</div>
              <div className={styles.featureDesc}>Password-protected sessions. Comments, approvals, and doodle annotations — all in real-time.</div>
            </div>
            <div className={styles.featureItem}>
              <div className={styles.featureIcon}>▣</div>
              <div className={styles.featureTitle}>Start in Previs Pro</div>
              <div className={styles.featureDesc}>The best tool that keeps you in control of blocking, framing and lighting. It&#39;s your film, it should be your vision — then enhance with A.I.</div>
            </div>
          </div>

          <div className={styles.footerLinks}>
            <a href="https://apps.apple.com/us/app/previs-pro-storyboard-fast/id1468484655" target="_blank" rel="noopener noreferrer">
              <img
                src="https://cdn.prod.website-files.com/65a5bcad20b210b1545fc7d4/65d8054a91fe50c89da8980d_Dl_on_the_App_Store_wt.svg"
                alt="Download on the App Store"
                className={styles.appStoreBadge}
              />
            </a>
            <a href="https://www.previspro.com" target="_blank" rel="noopener noreferrer" className={styles.learnMore}>
              Learn more
            </a>
          </div>
        </div>
      </div>
    );
  }

  // loading state
  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.spinner} />
        <p className={styles.message}>{statusMsg}</p>
      </div>
    </div>
  );
}
